import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { InventoryListParams, InventoryStockStatus } from '@/types/inventory';

export interface InventoryQueryState {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string;
  warehouseId: string;
  stockStatus: InventoryStockStatus;
}

type ParamPatch = Partial<{
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string | null;
  warehouseId: string | null;
  stockStatus: InventoryStockStatus | null;
}>;

const DEFAULTS: InventoryQueryState = {
  page: 1,
  limit: 10,
  sortBy: 'sku',
  sortOrder: 'asc',
  search: '',
  warehouseId: '',
  stockStatus: 'all',
};

function parseStockStatus(value: string | null): InventoryStockStatus {
  if (value === 'low' || value === 'out') return value;
  return 'all';
}

export function useInventoryQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<InventoryQueryState>(() => {
    const page = Number(searchParams.get('page') || DEFAULTS.page);
    const limit = Number(searchParams.get('limit') || DEFAULTS.limit);

    return {
      page: Number.isFinite(page) && page > 0 ? page : DEFAULTS.page,
      limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : DEFAULTS.limit,
      sortBy: searchParams.get('sortBy') || DEFAULTS.sortBy,
      sortOrder: searchParams.get('sortOrder') === 'desc' ? 'desc' : 'asc',
      search: searchParams.get('search') || '',
      warehouseId: searchParams.get('warehouseId') || '',
      stockStatus: parseStockStatus(searchParams.get('stockStatus')),
    };
  }, [searchParams]);

  const apiParams = useMemo<InventoryListParams>(
    () => ({
      page: state.page,
      limit: state.limit,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      ...(state.search ? { search: state.search } : {}),
      ...(state.warehouseId ? { warehouseId: state.warehouseId } : {}),
      ...(state.stockStatus !== 'all' ? { stockStatus: state.stockStatus } : {}),
    }),
    [state],
  );

  const patchParams = useCallback(
    (patch: ParamPatch, options?: { resetPage?: boolean }) => {
      setSearchParams(
        (searchParams) => {
          const next = new URLSearchParams(searchParams);
          const shouldResetPage = options?.resetPage ?? false;

          if (shouldResetPage) {
            next.set('page', '1');
          }

          const apply = (
            key: string,
            value: string | null | undefined,
            omitValues: string[] = [],
          ) => {
            if (value === null || value === undefined || value === '' || omitValues.includes(value)) {
              next.delete(key);
              return;
            }
            next.set(key, value);
          };

          if (patch.page !== undefined) next.set('page', String(patch.page));
          if (patch.limit !== undefined) next.set('limit', String(patch.limit));
          if (patch.sortBy !== undefined) next.set('sortBy', patch.sortBy);
          if (patch.sortOrder !== undefined) next.set('sortOrder', patch.sortOrder);
          if (patch.search !== undefined) apply('search', patch.search);
          if (patch.warehouseId !== undefined) apply('warehouseId', patch.warehouseId);
          if (patch.stockStatus !== undefined) apply('stockStatus', patch.stockStatus, ['all']);

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetAll = useCallback(() => {
    setSearchParams(
      (searchParams) => {
        const next = new URLSearchParams(searchParams);
        next.delete('search');
        next.delete('warehouseId');
        next.delete('stockStatus');
        next.delete('sortBy');
        next.delete('sortOrder');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const hasActiveFiltersOrSort = useMemo(
    () =>
      state.search !== '' ||
      state.stockStatus !== 'all' ||
      state.sortBy !== DEFAULTS.sortBy ||
      state.sortOrder !== DEFAULTS.sortOrder,
    [state],
  );

  return { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort };
}
